/**
 * «Пришёл по ссылке друга» — воркеру (владелец 03.10, ответ 1).
 *
 * Друг, который открыл чужую ссылку `?ref=` и потом поставил приложение или
 * начал заниматься, приносит пригласившему 2 000 и 3 000 баллов. Платит воркер
 * (cloudeflare/index.js, invitedArrive), но о том, что человек пришёл по
 * ссылке, знает только сайт — ссылку ловит shared/lib/referral.ts. Здесь две
 * вещи:
 *
 *  1. `stampReferralClick` — ссылку только что поймали (не дальше 10 минут):
 *     взять у воркера метку перехода (POST /affiliate/click, входа не нужно) —
 *     миг, подписанный воркером. Без неё воркер верит часам браузера не дальше
 *     двух суток назад — и тот, кто открыл ссылку, поставил приложение, а на
 *     сайт вошёл через неделю, остался бы неприглашённым.
 *  2. `reportReferralArrival` — человек вошёл и у него есть запомненная
 *     ссылка: POST /affiliate/arrive {handle, source: "site", capturedAt,
 *     click}. Один раз на ссылку и адрес (метка в localStorage); первое
 *     касание навсегда решает воркер, кто привёл — он не говорит.
 *
 * Ничего не бросает и ничего не показывает: это не влияет ни на одну страницу.
 */
import { authedFetch, getSessionToken, R2_WORKER_URL } from "@/features/bucket/api";
import { getStoredReferral, setReferralClick } from "@/shared/lib/referral";

const SENT_KEY = "referral_arrival_sent";
// Метку берём только в миг перехода (ссылку только что поймали): взятая позже
// показала бы переход позже, чем он был, — человеку хуже, чем без метки.
const STAMP_WINDOW_MS = 10 * 60 * 1000;
const stamping = new Set<string>();
const reporting = new Set<string>();

function currentEmail(): string {
    try {
        return (localStorage.getItem("site_user_email") || "").trim().toLowerCase();
    } catch {
        return "";
    }
}

/** Метка перехода для запомненной ссылки, если её ещё нет. → есть ли теперь метка. */
export async function stampReferralClick(now: number = Date.now()): Promise<boolean> {
    const stored = getStoredReferral(now);
    if (!stored) return false;
    if (stored.click) return true;
    if (now - stored.capturedAt > STAMP_WINDOW_MS) return false;
    if (stamping.has(stored.handle)) return false;
    stamping.add(stored.handle);
    try {
        const res = await fetch(`${R2_WORKER_URL}/affiliate/click`, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ handle: stored.handle }),
        });
        if (!res.ok) return false;
        const body = (await res.json().catch(() => null)) as { click?: string | null } | null;
        return !!(body && typeof body.click === "string" && setReferralClick(stored.handle, body.click));
    } catch {
        return false;
    } finally {
        stamping.delete(stored.handle);
    }
}

/**
 * Сообщить воркеру о приходе по ссылке, если человек вошёл и ещё не сообщали.
 * → "sent" | "skipped" (нечего или уже) | "failed" (повторим на следующей странице).
 */
export async function reportReferralArrival(now: number = Date.now()): Promise<"sent" | "skipped" | "failed"> {
    const stored = getStoredReferral(now);
    if (!stored) return "skipped";
    if (!getSessionToken()) return "skipped";
    const mark = `${stored.handle}|${currentEmail()}`;
    try {
        if (localStorage.getItem(SENT_KEY) === mark) return "skipped";
    } catch {
        /* нет хранилища — всё равно один раз на загрузку */
    }
    if (reporting.has(mark)) return "skipped";
    reporting.add(mark);
    try {
        const res = await authedFetch(`${R2_WORKER_URL}/affiliate/arrive`, {
            method: "POST",
            body: JSON.stringify({
                handle: stored.handle,
                source: "site",
                capturedAt: new Date(stored.capturedAt).toISOString(),
                ...(stored.click ? { click: stored.click } : {}),
            }),
        });
        if (!res.ok) {
            reporting.delete(mark);
            return "failed";
        }
        const body = (await res.json().catch(() => null)) as { ok?: boolean } | null;
        if (!body || body.ok !== true) {
            reporting.delete(mark);
            return "failed";
        }
        try {
            localStorage.setItem(SENT_KEY, mark);
        } catch {
            /* приватный режим — воркер всё равно ответит «уже» */
        }
        return "sent";
    } catch {
        reporting.delete(mark);
        return "failed";
    }
}

/** Для App: сначала метка перехода (если ссылка только что пришла), потом приход. */
export async function syncReferralArrival(now: number = Date.now()): Promise<void> {
    try {
        await stampReferralClick(now);
        await reportReferralArrival(now);
    } catch {
        /* никогда не мешает странице */
    }
}

/** Только для тестов. */
export function __resetReferralArrivalForTests(): void {
    stamping.clear();
    reporting.clear();
}
